import type { ReactElement } from 'react';
import CompactCard, { COMPACT_CARD_GRID } from '../../components/cards/CompactCard';
import {
  DECISION_ICON, DECISION_WORDS, decisionHover, notKnownHover,
  type DecisionCard, type DecisionList as Decisions,
} from './decisions';

interface DecisionListProps {
  list: Decisions;
}

/** One list's card, as its count stands now. Every state is the same card in the same place. */
function Card({ card }: { card: DecisionCard }): ReactElement {
  const common = { to: card.to, icon: DECISION_ICON[card.id], name: card.label };
  switch (card.state) {
    case 'loading':
      // Named at once; a grey bar where the number will be. What the number will count is its hover already.
      return <CompactCard {...common} loading hover={decisionHover(card.id, null, null)} />;
    case 'waiting':
      // Something waiting: an amber count on an amber square.
      return (
        <CompactCard
          {...common}
          figures={[{ value: card.count, label: DECISION_WORDS[card.id], tone: 'action' }]}
          iconTone="action"
          hover={decisionHover(card.id, card.hint, card.asOf)}
        />
      );
    case 'clear':
      // Nothing waiting: a light grey 0 with the same words. A zero the server keeps can be
      // some minutes old: when it was read is in the hover.
      return (
        <CompactCard
          {...common}
          figures={[{ value: 0, label: DECISION_WORDS[card.id], tone: 'zero' }]}
          hover={decisionHover(card.id, null, card.asOf)}
        />
      );
    case 'not-known':
    default:
      // No number (never a dash or a 0): a grey "Open", the same link, and why in the hover when the server said.
      return <CompactCard {...common} hover={notKnownHover(card.id, card.state === 'not-known' ? card.why : null)} />;
  }
}

/**
 * "Pending actions": one compact card per decision list the person holds, and the whole
 * card opens the list (components/cards/CompactCard). A card is the list's icon, its
 * short name and its count with a few grey words ("412 waiting"); what the count means
 * in full, how long the oldest has waited and when a kept count was read are its hover.
 *
 * The cards stand in one fixed order (decisions: DECISION_ORDER), each in its place from
 * the first render: a count on its way is a grey bar on the named card, an amber count
 * when something waits, a light grey 0 when nothing does, a grey "Open" when it is not
 * known. Nothing moves when a count lands. The block is left out when it has no card.
 *
 * It has no heading of its own: the home screen's tab strip names it.
 */
export default function DecisionList({ list }: DecisionListProps): ReactElement | null {
  if (list.cards.length === 0) return null;

  return (
    <section aria-label="Pending actions">
      <ul className={COMPACT_CARD_GRID}>
        {list.cards.map((card) => (
          <li key={card.id} className="min-w-0">
            <Card card={card} />
          </li>
        ))}
      </ul>
    </section>
  );
}
